/**
 * A small pool of meshing workers.
 *
 * Chunk rebuilds are the one job in this game that is both expensive and
 * trivially parallel: each chunk needs only its own padded volume, so any
 * number can be meshed at once without coordination.
 *
 * Jobs are queued, handed to whichever worker is free, and answered
 * asynchronously. The caller is responsible for ignoring or replacing results
 * that arrive for a chunk that has changed since - see the `version` field.
 */

import type { MeshRequest, MeshResponse } from "./meshWorker.js";

/** Leave a core for the main thread, and don't spawn more than we can feed. */
function defaultPoolSize(): number {
  const cores = navigator.hardwareConcurrency ?? 4;
  return Math.max(1, Math.min(4, cores - 1));
}

export class MeshWorkerPool {
  private readonly workers: Worker[] = [];
  private readonly idle: Worker[] = [];
  private readonly queue: MeshRequest[] = [];

  /** Chunk keys currently being meshed, so a chunk is never queued twice. */
  private readonly inFlight = new Set<string>();

  readonly size: number;

  constructor(onResult: (result: MeshResponse) => void, size = defaultPoolSize()) {
    this.size = size;

    for (let i = 0; i < size; i++) {
      const worker = new Worker(new URL("./meshWorker.ts", import.meta.url), {
        type: "module",
      });
      worker.onmessage = (event: MessageEvent<MeshResponse>): void => {
        this.inFlight.delete(event.data.key);
        onResult(event.data);
        this.release(worker);
      };
      worker.onerror = (event): void => {
        // A worker that has thrown is unlikely to recover; make the failure
        // loud rather than silently meshing nothing forever.
        console.error("mesh worker failed", event.message);
        this.release(worker);
      };
      this.workers.push(worker);
      this.idle.push(worker);
    }
  }

  /** True when this chunk already has a job queued or running. */
  isBusy(key: string): boolean {
    return this.inFlight.has(key);
  }

  get pending(): number {
    return this.inFlight.size;
  }

  submit(request: MeshRequest): void {
    if (this.inFlight.has(request.key)) return;
    this.inFlight.add(request.key);

    const worker = this.idle.pop();
    if (worker === undefined) {
      this.queue.push(request);
      return;
    }
    this.dispatch(worker, request);
  }

  private dispatch(worker: Worker, request: MeshRequest): void {
    // Both arrays are freshly copied per job, so both can be transferred
    // rather than cloned - which for a chunk's worth of bytes is the whole
    // difference between meshing off the main thread and only pretending to.
    worker.postMessage(request, [request.volume.buffer, request.light.buffer]);
  }

  private release(worker: Worker): void {
    const next = this.queue.shift();
    if (next === undefined) {
      this.idle.push(worker);
      return;
    }
    this.dispatch(worker, next);
  }

  dispose(): void {
    for (const w of this.workers) w.terminate();
    this.workers.length = 0;
    this.idle.length = 0;
    this.queue.length = 0;
    this.inFlight.clear();
  }
}
